/** Where the guest was when a masked error arrived: nothing in it identifies the guest or the ticket. */
export interface PageContext {
	/** The first segment of the path (`ticket`, `payment`, …); the rest carries the location and ticket ids. */
	readonly page: string
	readonly visibility: DocumentVisibilityState
	readonly sinceLoadMs: number
	/** Hosts of the other-origin scripts on the page: which payment SDKs had been loaded. */
	readonly scriptHosts: readonly string[]
	/** Hosts of the other-origin frames on the page: which payment form was mounted. */
	readonly frameHosts: readonly string[]
}

const otherOriginHosts = (urls: readonly string[], origin: string): readonly string[] => {
	// `about:blank` frames and inline scripts have no host.
	const hosts = urls
		.filter((url) => url !== '')
		.map((url) => new URL(url, origin))
		.filter((url) => url.origin !== origin && url.host !== '')
		.map((url) => url.host)

	return [...new Set(hosts)]
}

/** Describes the page as it is right now. */
export const pageContext = (document: Document, performance: Performance): PageContext => {
	const { origin, pathname } = document.location

	return {
		page: pathname.split('/')[1] ?? '',
		visibility: document.visibilityState,
		sinceLoadMs: Math.round(performance.now()),
		scriptHosts: otherOriginHosts(
			Array.from(document.scripts, (script) => script.src),
			origin
		),
		frameHosts: otherOriginHosts(
			Array.from(document.querySelectorAll('iframe'), (frame) => frame.src),
			origin
		),
	}
}
